/** The Document timeline's words: what happened to a file, as a person would tell it. Never a lease, a fence or a model. Pure. */
import type { Formatter } from '@paige/format';
import { reasonPhrase } from '../insights/reason-words';
import type { FileStory, StoryStep } from './timeline-types';

export type StoryTone = 'ok' | 'live' | 'wait' | 'stop' | 'off';

const STATE: Record<string, { word: string; tone: StoryTone }> = {
  receiving: { word: 'Arriving', tone: 'live' },
  held: { word: 'Held', tone: 'wait' },
  processing: { word: 'Being worked', tone: 'live' },
  review: { word: 'Waiting for a person to review', tone: 'wait' },
  exception: { word: 'Needs someone', tone: 'stop' },
  needs_attention: { word: 'Needs someone', tone: 'stop' },
  accepted: { word: 'Accepted, going out', tone: 'live' },
  exported: { word: 'Exported', tone: 'ok' },
  complete: { word: 'Done', tone: 'ok' },
  cancelled: { word: 'Cancelled', tone: 'off' },
};

/** Where a file or a document stands, in a few words. */
export function stateWord(state: string): { word: string; tone: StoryTone } {
  return STATE[state] ?? { word: state.replace(/_/g, ' '), tone: 'off' };
}

const SOURCE: Record<string, string> = {
  api: 'through the API',
  upload: 'as a browser upload',
  email: 'by email',
  folder: 'from a watched folder',
  scanner: 'from Paige Scan',
  mobile: 'from Paige Mobile',
  demo: 'as sample pages',
};

/** "through the API", "from Paige Scan". */
export function arrivedHow(source: string): string {
  return SOURCE[source] ?? `by ${source}`;
}

/** A stage as [it is done, it is happening, it has not started]. */
const STAGE: Record<string, [string, string, string]> = {
  intake: ['Received and checked', 'Being checked in', 'Waiting to be checked in'],
  render: ['Pages prepared', 'Preparing pages', 'Waiting for page preparation'],
  ocr: ['Pages read', 'Reading pages', 'Waiting for the page reader'],
  split: ['Split into documents', 'Splitting into documents', 'Waiting to be split'],
  classify: ['Type recognised', 'Recognising the type', 'Waiting for understanding'],
  extract: ['Fields understood', 'Understanding the fields', 'Waiting for understanding'],
  check: ['Checked', 'Checking', 'Waiting to be checked'],
  handoff: [
    "Handed to the job's engine",
    "Handing to the job's engine",
    "Waiting for the job's engine",
  ],
  review: ['Reviewed by Cortexa', 'Cortexa is reviewing', 'Waiting for review'],
  export: ['Export prepared', 'Exporting', 'Waiting to export'],
  learn: ['Learned from', 'Learning', 'Waiting to learn'],
};

const FIXED: Record<string, string> = {
  capture_handed: "Handed to the job's engine",
  capture_read: 'Read by the engine',
  capture_parked: 'The engine could not read it',
  file_refused: 'A file was not read',
  charged: 'Charged',
};

const ACTION: Record<string, string> = {
  'document.confirmed': 'Confirmed by a person',
  'problems.resolved': 'Closed by staff',
  'problems.retried': 'Retried by staff',
};

function stageTitle(step: StoryStep): string {
  const [did, doing, waiting] = STAGE[step.key] ?? [step.key, step.key, step.key];
  switch (step.state) {
    case 'done':
      return did;
    case 'working':
      return doing;
    case 'parked':
      return `${doing}: stopped`;
    case 'retrying':
      return `${doing}: trying again`;
    case 'cancelled':
      return `${doing}: closed by staff`;
    case 'held':
      return `${waiting}: held`;
    default:
      return waiting;
  }
}

function deliveryTitle(step: StoryStep): string {
  const to = step.destinationLabel ?? step.destination ?? 'a destination';
  if (step.state === 'done') return `Delivered to ${to}`;
  if (step.state === 'failed') return `Delivery to ${to} failed`;
  return step.state === 'working' ? `Delivering to ${to}` : `Delivery to ${to}: ${step.state}`;
}

/** How a document left review, and by whom (migration 0271). */
function confirmedTitle(step: StoryStep): string {
  switch (step.how) {
    case 'person':
      return `Confirmed by ${step.who ?? 'a person'}`;
    case 'api_key':
      return 'Confirmed through the API';
    case 'ai_review':
      return 'Confirmed by Cortexa’s AI review';
    case 'full_auto':
      return 'Released by Autopilot without a check';
    default:
      return 'Released: the job no longer held it for a person';
  }
}

const ROLE: Record<string, string> = {
  admin: 'as an admin',
  job_manager: 'as a job manager',
  reviewer: 'as a reviewer',
  staff: 'as Ademero staff',
};

function confirmedFacts(f: Formatter, step: StoryStep): Facts {
  return [
    step.role ? (ROLE[step.role] ?? null) : null,
    step.count > 1 ? `${f.int(step.count)} documents` : null,
  ];
}

function actionTitle(step: StoryStep): string {
  const name = step.stage ?? 'An action';
  return ACTION[name] ?? name.replace(/[._]/g, ' ');
}

/** The step's headline. */
export function stepTitle(step: StoryStep, story: Pick<FileStory, 'source'>): string {
  if (step.key === 'arrived') return `Arrived ${arrivedHow(story.source)}`;
  if (step.key === 'action') return actionTitle(step);
  if (step.key === 'delivery') return deliveryTitle(step);
  if (step.key === 'file_refused' && step.stage) return `${step.stage} was not read`;
  if (step.key === 'confirmed') return confirmedTitle(step);
  return FIXED[step.key] ?? stageTitle(step);
}

/** The step's tone, for its mark: done, moving, waiting, stopped or closed. */
export function stepTone(step: StoryStep): StoryTone {
  if (step.state === 'done') return 'ok';
  if (step.state === 'working') return 'live';
  if (step.state === 'parked' || step.state === 'failed') return 'stop';
  if (step.state === 'cancelled' || step.state === 'superseded') return 'off';
  return 'wait';
}

const pagesOf = (f: Formatter, n: number) => `${f.int(n)} ${n === 1 ? 'page' : 'pages'}`;

/** How long the work itself took, when the workers reported it; under a second reads as milliseconds. */
function workWords(f: Formatter, ms: number): string | null {
  if (ms <= 0) return null;
  return ms < 1000 ? `${f.int(ms)} ms of work` : `${f.duration(Math.round(ms / 1000))} of work`;
}

type Facts = (string | null)[];

function chargedFacts(f: Formatter, step: StoryStep): Facts {
  const split = step.meters.length > 1 ? step.meters : [];
  return [
    `${f.decimal(step.credits ?? 0, 2)} pages`,
    ...split.map((m) => `${m.meter} ${f.decimal(m.credits, 2)}`),
  ];
}

/** A bare reference reads "filed as X"; a sentence the destination wrote is said as it is. */
export function filedAs(receipt: string): string {
  return receipt.includes(' ') ? receipt : `filed as ${receipt}`;
}

function deliveryFacts(f: Formatter, step: StoryStep): Facts {
  // Several deliveries are listed one by one under the step, each with its own tries (deliveryLines).
  if (step.deliveries.length > 1) {
    const failed = step.deliveries.filter((d) => d.state === 'failed').length;
    return [
      `${f.int(step.deliveries.length)} deliveries`,
      failed > 0 ? `${f.int(failed)} failed` : null,
    ];
  }
  return [
    step.attempts > 1 ? `${f.int(step.attempts)} tries` : null,
    step.receipt ? filedAs(step.receipt) : null,
  ];
}

/** Each delivery of a destination's step, when there are several: what it carried, its tries, where it went. */
export function deliveryLines(
  f: Formatter,
  step: StoryStep,
  story: Pick<FileStory, 'documents'>,
): { at: string; text: string; failed: boolean }[] {
  if (step.deliveries.length < 2) return [];
  return step.deliveries.map((d) => {
    const document = story.documents.find((doc) => doc.id === d.documentId);
    const what =
      document?.typeName ?? (d.documentId ? `document ${d.documentId.slice(0, 8)}` : 'a document');
    const tries = d.tries > 1 ? `${f.int(d.tries)} tries` : d.tries === 1 ? '1 try' : null;
    const end =
      d.state === 'done'
        ? d.receipt
          ? filedAs(d.receipt)
          : 'delivered'
        : d.state === 'failed'
          ? `failed${d.detail ? `: ${d.detail}` : ''}`
          : d.state;
    return {
      at: d.at,
      text: [what, tries, end].filter(Boolean).join(' · '),
      failed: d.state === 'failed',
    };
  });
}

/** "6 pages", or "4 of 6 pages" while some are still to do; only where the stage works page by page. */
function pagesFact(f: Formatter, step: StoryStep): string | null {
  const paged = step.key === 'render' || step.key === 'ocr';
  if (!paged || step.count === 0) return null;
  return step.done === step.count
    ? pagesOf(f, step.count)
    : `${f.int(step.done)} of ${pagesOf(f, step.count)}`;
}

function stageFacts(f: Formatter, step: StoryStep): Facts {
  const retries = step.attempts - step.count;
  const waited = step.waitedSeconds ?? 0;
  return [
    pagesFact(f, step),
    workWords(f, step.workMs),
    waited >= 5 ? `waited ${f.duration(waited)} for its turn` : null,
    retries > 0 ? `${f.int(retries)} extra ${retries === 1 ? 'try' : 'tries'}` : null,
  ];
}

function factsOf(f: Formatter, step: StoryStep, story: Pick<FileStory, 'files' | 'pages'>): Facts {
  switch (step.key) {
    case 'arrived':
      return [pagesOf(f, story.pages), story.files > 1 ? `${f.int(story.files)} files` : null];
    case 'charged':
      return chargedFacts(f, step);
    case 'action':
      return [step.who ?? (step.reason === 'staff' ? 'Ademero staff' : 'someone at the company')];
    case 'delivery':
      return deliveryFacts(f, step);
    case 'confirmed':
      return confirmedFacts(f, step);
    default:
      return stageFacts(f, step);
  }
}

/** The facts under a step's title, each a short phrase. */
export function stepFacts(
  f: Formatter,
  step: StoryStep,
  story: Pick<FileStory, 'files' | 'pages'>,
): string[] {
  return factsOf(f, step, story).filter((x): x is string => x !== null);
}

/** Why a step stopped, waits or is being tried again; null when it simply ran. */
export function stepTrouble(step: StoryStep): string | null {
  if (step.state === 'parked' || step.state === 'retrying' || step.state === 'failed') {
    const why = step.reason ? reasonPhrase(step.reason) : null;
    return [why, step.detail].filter(Boolean).join(': ') || null;
  }
  if (step.state === 'cancelled') return step.detail;
  if (step.key === 'capture_parked') return step.detail;
  return null;
}

/** "Put back to wait 3 times: the job has not been set up yet". */
export function stepHeld(f: Formatter, step: StoryStep): string | null {
  if (step.held === 0) return null;
  const times = step.held === 1 ? 'once' : `${f.int(step.held)} times`;
  return `Put back to wait ${times} without spending a try${step.heldDetail ? `: ${step.heldDetail}` : ''}`;
}

/** The quiet stretch before a step, when it is long enough to wonder about. */
export function gapBefore(step: StoryStep, previous: StoryStep | undefined): number {
  if (!previous || step.key === 'charged') return 0;
  const from = Date.parse(previous.endedAt ?? previous.at);
  const gap = Math.round((Date.parse(step.at) - from) / 1000);
  return gap >= 60 ? gap : 0;
}

/** The story in one sentence, under the file's name. */
export function storyLine(f: Formatter, story: FileStory): string {
  const took = Math.max(
    0,
    Math.round((Date.parse(story.lastAt) - Date.parse(story.receivedAt)) / 1000),
  );
  const span =
    story.state === 'exported' || story.state === 'complete' ? `, done in ${f.duration(took)}` : '';
  const cost = story.credits > 0 ? `, ${f.decimal(story.credits, 2)} pages` : '';
  return `Arrived ${arrivedHow(story.source)} ${f.dateTime(story.receivedAt)}${span}${cost}.`;
}

/** What became of an arrival, for the finder's list: "3 documents, 2 exported, 1 for review". */
export function outcomeWords(
  f: Formatter,
  file: { documents: number; exported: number; inReview: number; exceptions: number },
): string {
  if (file.documents === 0) return 'no document yet';
  const parts = [
    `${f.int(file.documents)} ${file.documents === 1 ? 'document' : 'documents'}`,
    file.exported > 0 && file.exported < file.documents ? `${f.int(file.exported)} exported` : null,
    file.inReview > 0 ? `${f.int(file.inReview)} for review` : null,
    file.exceptions > 0 ? `${f.int(file.exceptions)} needing someone` : null,
  ];
  return parts.filter(Boolean).join(', ');
}
